/**
 * assistant-modules/emotion-wheel.ts
 * ════════════════════════════════════════════════════════════════════════════
 * Gloria Wilcox Feelings Wheel — Frontend Visualizer (TypeScript)
 *
 * Provides:
 *  • EmotionWheelVisualizer class
 *      .render(canvasEl, analysis)              — full 3-ring canvas visualization
 *      .injectMiniBadge(messageEl, analysis)    — inline chat bubble badge
 *      .showTransformationDrawer(analysis)      — slide-in reframe panel (intensity >= 7)
 *      .hideTransformationDrawer()              — dismiss active drawer
 *      .getColor(primary)                       — canonical primary → hex colour
 *
 * Consumed by main.ts via named import — no window global needed.
 * Handlers dispatch `akili:emotion-analysis` CustomEvents; main.ts wires
 * those events to EmotionWheelVisualizer methods.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

/** The six Wilcox primary (core) emotions. */
export type WilcoxPrimary =
  | 'mad'
  | 'sad'
  | 'scared'
  | 'joyful'
  | 'powerful'
  | 'peaceful';

/**
 * The emotion analysis payload arriving from the backend SSE meta event.
 * Field names use snake_case to match the server JSON response exactly.
 */
export interface EmotionAnalysis {
  /** One of the six Wilcox primaries. */
  primary:                  WilcoxPrimary;
  /** Secondary emotion (e.g. "frustrated", "anxious"). */
  secondary:                string;
  /** Tertiary emotion — the most specific label (e.g. "infuriated", "overwhelmed"). */
  tertiary:                 string;
  /** Intensity on a 1–10 scale. */
  intensity:                number;
  /** Diametric opposite primary. */
  opposite_primary:         string;
  /** Diametric opposite tertiary — the reframe target. */
  opposite_tertiary:        string;
  /** LLM-generated description: "The user feels X because Y…" */
  pinpoint_description:     string;
  /** LLM-generated reframe suggestion: "To move toward Z, consider…" */
  transformation_suggestion: string;
}

/** Colour map keyed by WilcoxPrimary. */
type PrimaryColorMap = Record<WilcoxPrimary, string>;

/** Emoji map keyed by WilcoxPrimary. */
type PrimaryEmojiMap = Record<WilcoxPrimary, string>;

// ─── Constants ────────────────────────────────────────────────────────────────

const EMOTION_COLORS: PrimaryColorMap = {
  mad:      '#e74c3c',
  sad:      '#3498db',
  scared:   '#9b59b6',
  joyful:   '#f39c12',
  powerful: '#27ae60',
  peaceful: '#1abc9c',
};

const EMOTION_EMOJIS: PrimaryEmojiMap = {
  mad:      '😤',
  sad:      '😔',
  scared:   '😨',
  joyful:   '😊',
  powerful: '💪',
  peaceful: '😌',
};

// ─── Singleton drawer state ───────────────────────────────────────────────────

let _activeDrawerTimeout: ReturnType<typeof setTimeout> | null = null;
let _activeDrawerEl:      HTMLElement | null                   = null;

/** Last analysis shown — stored so the "Full view" button can reference it. */
let _lastAnalysis: EmotionAnalysis | null = null;

// ─── EmotionWheelVisualizer ───────────────────────────────────────────────────

export class EmotionWheelVisualizer {

  // ── Full canvas wheel ───────────────────────────────────────────────────────

  /**
   * Render a full 3-ring wheel into `canvas`.
   * Outer ring = intensity arc; middle ring = secondary label zone;
   * inner fill = primary colour; centre labels show tertiary + opposite.
   *
   * Called when the user clicks the mini badge for the detail modal.
   */
  static render(canvas: HTMLCanvasElement, analysis: EmotionAnalysis): void {
    if (!canvas || !analysis) return;

    const { primary, secondary, tertiary, intensity, opposite_tertiary } = analysis;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W  = canvas.width  || 300;
    const H  = canvas.height || 300;
    const cx = W / 2;
    const cy = H / 2;
    const col = EmotionWheelVisualizer.getColor(primary);

    ctx.clearRect(0, 0, W, H);

    // ── Outer ring: intensity arc ───────────────────────────────────────────
    const intensityArc = (intensity / 10) * Math.PI * 2;

    // Grey track first (drawn behind the coloured arc)
    ctx.beginPath();
    ctx.arc(cx, cy, cx * 0.92, 0, Math.PI * 2);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth   = 4;
    ctx.stroke();

    // Coloured progress arc
    ctx.beginPath();
    ctx.arc(cx, cy, cx * 0.92, -Math.PI / 2, -Math.PI / 2 + intensityArc);
    ctx.strokeStyle = col;
    ctx.lineWidth   = 14;
    ctx.lineCap     = 'round';
    ctx.stroke();

    // ── Middle ring: secondary emotion zone ────────────────────────────────
    ctx.beginPath();
    ctx.arc(cx, cy, cx * 0.62, 0, Math.PI * 2);
    ctx.strokeStyle = col + '60';  // 38% opacity hex suffix
    ctx.lineWidth   = 28;
    ctx.stroke();

    // ── Inner fill: primary colour ─────────────────────────────────────────
    ctx.beginPath();
    ctx.arc(cx, cy, cx * 0.32, 0, Math.PI * 2);
    ctx.fillStyle = col + '22';    // 13% opacity hex suffix
    ctx.fill();
    ctx.strokeStyle = col;
    ctx.lineWidth   = 2;
    ctx.stroke();

    // ── Text labels ────────────────────────────────────────────────────────
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';

    // Primary emoji (centre)
    ctx.font = `${cx * 0.28}px serif`;
    ctx.fillStyle = '#000';
    ctx.fillText(EMOTION_EMOJIS[primary] ?? '🤔', cx, cy - cx * 0.04);

    // Tertiary emotion (bold, below emoji)
    ctx.font      = `bold ${cx * 0.12}px 'Montserrat', system-ui, sans-serif`;
    ctx.fillStyle = '#111827';
    ctx.fillText(_cap(tertiary), cx, cy + cx * 0.16);

    // Secondary emotion (small, above emoji)
    ctx.font      = `${cx * 0.09}px system-ui, sans-serif`;
    ctx.fillStyle = '#6b7280';
    ctx.fillText(_cap(secondary), cx, cy - cx * 0.20);

    // Intensity score
    ctx.font      = `bold ${cx * 0.11}px 'Montserrat', system-ui, sans-serif`;
    ctx.fillStyle = col;
    ctx.fillText(`${intensity}/10`, cx, cy + cx * 0.34);

    // Opposite-emotion arrow (bottom of canvas)
    if (opposite_tertiary) {
      ctx.font      = `${cx * 0.09}px system-ui, sans-serif`;
      ctx.fillStyle = '#059669';
      ctx.fillText(`→ ${_cap(opposite_tertiary)}`, cx, H - 16);
    }
  }

  // ── Inline mini badge ───────────────────────────────────────────────────────

  /**
   * Inject a compact emotion badge into the given bot message element.
   * Replaces any pre-existing badge on the same element.
   *
   * The badge shows: emoji | tertiary label | animated intensity bar | opposite arrow.
   * Clicking the badge opens the full canvas detail modal.
   */
  static injectMiniBadge(messageEl: HTMLElement, analysis: EmotionAnalysis): void {
    if (!messageEl || !analysis) return;

    // Remove any stale badge from a previous render on this element
    messageEl.querySelector('.emotion-wheel-mini')?.remove();

    const { primary, tertiary, intensity, opposite_tertiary } = analysis;
    const col    = EmotionWheelVisualizer.getColor(primary);
    const emoji  = EMOTION_EMOJIS[primary] ?? '🤔';
    const barPct = Math.round((intensity / 10) * 100);

    const badge = document.createElement('div');
    badge.className = 'emotion-wheel-mini';
    badge.setAttribute('data-primary', primary);
    badge.setAttribute('role', 'complementary');
    badge.setAttribute('aria-label', `Kili sensed: ${tertiary} (intensity ${intensity}/10)`);

    badge.innerHTML = `
      <div class="ew-badge" style="border-left-color:${col};" title="Click to expand emotion view">
        <span class="ew-emoji">${emoji}</span>
        <div class="ew-info">
          <span class="ew-tertiary">${_cap(tertiary)}</span>
          <div class="ew-bar-track">
            <div class="ew-bar-fill"
                 style="width:0%;background:${col};"
                 data-intensity="${intensity}">
            </div>
          </div>
        </div>
        <span class="ew-intensity-label">${intensity}<small>/10</small></span>
      </div>
      ${opposite_tertiary ? `
      <div class="ew-transform-hint" title="Reframe path">
        <span class="ew-arrow">→</span>
        <span class="ew-opposite" style="color:${col};">${_cap(opposite_tertiary)}</span>
      </div>` : ''}
    `;

    // Click badge → open full detail modal
    badge.querySelector<HTMLElement>('.ew-badge')?.addEventListener('click', () => {
      EmotionWheelVisualizer._showDetailModal(analysis);
    });

    messageEl.appendChild(badge);

    // Animate bar width after paint (start at 0 so transition fires)
    requestAnimationFrame(() => {
      const fill = badge.querySelector<HTMLElement>('.ew-bar-fill');
      if (fill) {
        fill.style.transition = 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        fill.style.width      = `${barPct}%`;
      }
    });
  }

  // ── Transformation drawer ───────────────────────────────────────────────────

  /**
   * Show the emotional reframe panel at the top of the AI drawer.
   * Triggered when intensity >= 7. Auto-dismisses after 45 seconds.
   * Only one drawer is active at a time — any previous one is removed first.
   */
  static showTransformationDrawer(analysis: EmotionAnalysis): void {
    if (!analysis) return;

    const drawerEl = document.getElementById('ai-drawer');
    if (!drawerEl) return;

    // Dismiss any currently displayed drawer before inserting a new one
    EmotionWheelVisualizer.hideTransformationDrawer();

    _lastAnalysis = analysis;

    const {
      primary,
      tertiary,
      opposite_tertiary,
      pinpoint_description,
      transformation_suggestion,
      intensity,
    } = analysis;

    const col   = EmotionWheelVisualizer.getColor(primary);
    const emoji = EMOTION_EMOJIS[primary] ?? '🤔';

    const panel = document.createElement('div');
    panel.className = 'transformation-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Emotional reframe suggestion');

    panel.innerHTML = `
      <div class="tp-header">
        <div class="tp-title">
          <span class="tp-emoji">${emoji}</span>
          <h4>Emotional Reframe</h4>
          <span class="tp-intensity" style="background:${col}33;color:${col};">${intensity}/10</span>
        </div>
        <button class="tp-close" aria-label="Dismiss reframe">×</button>
      </div>
      <p class="tp-pinpoint">${pinpoint_description ?? ''}</p>
      <div class="tp-path">
        <div class="tp-state current" style="border-color:${col};color:${col};">
          ${_cap(tertiary)}
        </div>
        <div class="tp-arrow">→</div>
        <div class="tp-state target" style="border-color:#059669;color:#059669;">
          ${_cap(opposite_tertiary ?? 'calm')}
        </div>
      </div>
      <p class="tp-suggestion">${transformation_suggestion ?? ''}</p>
      <div class="tp-footer">
        <button class="tp-btn-dismiss">Got it</button>
        <button class="tp-btn-expand">Full view</button>
      </div>
    `;

    // Wire buttons with typed event listeners (no inline onclick attributes)
    panel.querySelector<HTMLButtonElement>('.tp-close')
      ?.addEventListener('click', () => EmotionWheelVisualizer.hideTransformationDrawer());

    panel.querySelector<HTMLButtonElement>('.tp-btn-dismiss')
      ?.addEventListener('click', () => EmotionWheelVisualizer.hideTransformationDrawer());

    panel.querySelector<HTMLButtonElement>('.tp-btn-expand')
      ?.addEventListener('click', () => {
        if (_lastAnalysis) EmotionWheelVisualizer._showDetailModal(_lastAnalysis);
      });

    drawerEl.insertBefore(panel, drawerEl.firstChild);
    _activeDrawerEl = panel;

    // Trigger CSS slide-in animation
    requestAnimationFrame(() => panel.classList.add('tp-visible'));

    // Auto-dismiss after 45 seconds
    _activeDrawerTimeout = setTimeout(
      () => EmotionWheelVisualizer.hideTransformationDrawer(),
      45_000,
    );
  }

  /**
   * Dismiss and remove the active transformation drawer, clearing any
   * pending auto-dismiss timer.
   */
  static hideTransformationDrawer(): void {
    if (_activeDrawerTimeout !== null) {
      clearTimeout(_activeDrawerTimeout);
      _activeDrawerTimeout = null;
    }
    if (_activeDrawerEl) {
      _activeDrawerEl.classList.add('tp-hiding');
      const el = _activeDrawerEl;
      setTimeout(() => { el.remove(); }, 300);
      _activeDrawerEl = null;
    }
  }

  // ── Detail modal ────────────────────────────────────────────────────────────

  /**
   * Open a modal containing the full 300×300 canvas wheel.
   * Clicking the backdrop or the ×  button closes the modal.
   *
   * @internal — called by injectMiniBadge click and the "Full view" button.
   */
  static _showDetailModal(analysis: EmotionAnalysis): void {
    if (!analysis) return;

    // Remove any previously open detail modal
    document.querySelector('.ew-detail-modal')?.remove();

    const modal = document.createElement('div');
    modal.className = 'ew-detail-modal';

    modal.innerHTML = `
      <div class="ew-modal-backdrop"></div>
      <div class="ew-modal-content">
        <div class="ew-modal-header">
          <h3>Emotional State</h3>
          <button class="ew-modal-close" aria-label="Close">×</button>
        </div>
        <canvas id="ew-canvas-full" width="260" height="260"></canvas>
        <div class="ew-modal-details">
          <div class="ew-detail-row"><strong>Primary</strong><span>${_cap(analysis.primary)}</span></div>
          <div class="ew-detail-row"><strong>Secondary</strong><span>${_cap(analysis.secondary)}</span></div>
          <div class="ew-detail-row"><strong>Tertiary</strong><span>${_cap(analysis.tertiary)}</span></div>
          <div class="ew-detail-row"><strong>Intensity</strong><span>${analysis.intensity}/10</span></div>
          <div class="ew-detail-row">
            <strong>Reframe path</strong>
            <span style="color:#059669;">${_cap(analysis.opposite_tertiary)}</span>
          </div>
        </div>
      </div>
    `;

    // Wire close handlers (no inline onclick — typed listeners only)
    const close = () => modal.remove();
    modal.querySelector<HTMLElement>('.ew-modal-backdrop')?.addEventListener('click', close);
    modal.querySelector<HTMLButtonElement>('.ew-modal-close')?.addEventListener('click', close);

    document.body.appendChild(modal);

    // CSS visibility class triggers the scale-in animation
    requestAnimationFrame(() => modal.classList.add('ew-modal-visible'));

    // Render the canvas after the modal is in the DOM
    requestAnimationFrame(() => {
      const canvas = modal.querySelector<HTMLCanvasElement>('#ew-canvas-full');
      if (canvas) EmotionWheelVisualizer.render(canvas, analysis);
    });
  }

  // ── Colour lookup ───────────────────────────────────────────────────────────

  /**
   * Return the canonical hex colour for a Wilcox primary emotion.
   * Falls back to neutral grey for unknown primaries.
   */
  static getColor(primary: string): string {
    return EMOTION_COLORS[primary as WilcoxPrimary] ?? '#6b7280';
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Capitalise the first character of a string. */
function _cap(str: string | null | undefined): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
